import { Button } from '@getsynapse/design-system';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import { useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import { SLICE_STATUS } from 'utils/constants';
import { BudgetModel } from 'utils/customTypes';
import {
  fetchBudgetAmountsByBudgetId,
  fetchBudgets,
  fetchBudgetSettings,
  fetchCostOfLaborActuals,
  fetchLineItemsAmountsByBudgetId,
  getLineItemStatus,
  getSelectedBudget,
  getShowBrokenModal,
  setShowBrokenModal,
} from '../../state/BudgetList/budgetListSlice';
import { fetchPrograms } from '../../state/Programs/programsSlice';
import { fetchProjects } from '../../state/Projects/projectsSlice';
import { getLDTeams } from '../../state/Teams/teamsSlice';
import { getVendors } from '../../state/Vendors/vendorsSlice';
import { BrokenRelationshipModal } from './components/BrokenRelationshipModal';
import useBrokenBudgetOptions from './hooks/useBrokenBudgetOptions';
import BudgetManage from './tabs/Manage/BudgetManage';
import { CreateBudgetModal } from './tabs/Manage/components/modals/CreateBudgetModal';
import withPagePermission from 'Pages/PermissionChecker/withPagePermission';

const BudgetsPageContent = () => {
  const dispatch = useDispatch();
  const [displayBrokenRelationshipModal, setDisplayBrokenRelationshipModal] =
    useState(false);
  const { brokenLineItems } = useBrokenBudgetOptions();
  const showBrokenModal = useSelector(getShowBrokenModal);
  const selectedBudget = useSelector(getSelectedBudget);
  const lineItemStatus = useSelector(getLineItemStatus);

  useEffect(() => {
    dispatch(fetchBudgets());
    dispatch(fetchProjects());
    dispatch(fetchPrograms());
    dispatch(getLDTeams());
    dispatch(getVendors());
    dispatch(fetchBudgetSettings());
    dispatch(setShowBrokenModal(true));
  }, [dispatch]);

  useEffect(() => {
    setDisplayBrokenRelationshipModal(
      brokenLineItems.length > 0 && showBrokenModal
    );
  }, [brokenLineItems, showBrokenModal]);

  useEffect(() => {
    if (selectedBudget) {
      dispatch(fetchBudgetAmountsByBudgetId(selectedBudget.id));
      dispatch(fetchLineItemsAmountsByBudgetId(selectedBudget.id));
    }
  }, [dispatch, selectedBudget]);

  useEffect(() => {
    if (selectedBudget && lineItemStatus === SLICE_STATUS.SUCCESS) {
      dispatch(fetchCostOfLaborActuals(selectedBudget.id));
    }
  }, [lineItemStatus, selectedBudget, dispatch]);

  const [displayCreateBudgetModal, setDisplayCreateBudgetModal] =
    useState(false);

  const handleAddBudget = () => {
    setDisplayCreateBudgetModal(true);
  };
  const {
    isNotificationVisible,
    snackbarProps,
    showNotification,
    SnackbarNotification,
  } = useSnackbarNotification();

  function onCreateBudget(data: BudgetModel) {
    try {
      setDisplayCreateBudgetModal(false);
      showNotification({
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.CREATE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_CREATION_SUCCESS',
          { title: data.title }
        ),
        hasActionButton: true,
      });
    } catch (error) {
      showNotification({
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.CREATE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_CREATION_ERROR',
          { title: data.title }
        ),
        hasActionButton: false,
      });
    }
  }

  return (
    <div className='flex flex-col h-full p-6'>
      <div className='flex flex-row justify-between'>
        <div className='font-bold text-xl'>{intl.get('BUDGET_PAGE.TITLE')}</div>
        <Button onClick={handleAddBudget} data-cy='add-budget-button'>
          {intl.get('BUDGET_PAGE.CREATE_BUDGET_MODAL.TITLE')}
        </Button>
      </div>
      <BudgetManage />

      <BrokenRelationshipModal
        isOpen={displayBrokenRelationshipModal}
        closeModal={() => {
          setDisplayBrokenRelationshipModal(false);
          dispatch(setShowBrokenModal(false));
        }}
        onCancel={() => setDisplayBrokenRelationshipModal(false)}
      />

      <CreateBudgetModal
        isOpen={displayCreateBudgetModal}
        closeModal={() => setDisplayCreateBudgetModal(false)}
        onCancel={() => setDisplayCreateBudgetModal(false)}
        onCreateBudget={onCreateBudget}
      />
      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}
    </div>
  );
};

const BudgetsPage = withPagePermission(BudgetsPageContent, {
  pageName: 'budget',
});

export default BudgetsPage;
